import {AppApi} from 'store/customer/api';
import {
    RoomPlannerLayout,
    CreateLayoutRequest,
    GetLayoutsResponse,
    UpdateCabinetPositionRequest,
    UpdatePlaceholderPositionRequest,
    CreateLayoutResponse,
    LayoutRoomPlaceholder,
} from 'components/customer/RoomPlanner/types';

export const roomPlannerApi = AppApi.injectEndpoints({
    endpoints: (builder) => ({
        duplicateProduct: builder.mutation<
            {job_cabinet_id: number; type: number},
            {roomId: number; jobCabinetId: number}
        >({
            query: ({roomId, jobCabinetId}) => {
                return {
                    url: `cabinets/jobcabinet/duplicate/roomId/${roomId}/jobCabinetId/${jobCabinetId}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: {
                cabinet: {job_cabinet_id: number; type: number};
            }) => {
                return response.cabinet;
            },
        }),
        createLayout: builder.mutation<
            CreateLayoutResponse,
            CreateLayoutRequest
        >({
            query: ({roomId, ...data}) => ({
                url: `room-planner/layouts/${roomId}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['RoomPlannerLayout'],
        }),
        getLayouts: builder.query<GetLayoutsResponse, number>({
            query: (roomId) => ({
                url: `room-planner/layouts/${roomId}`,
                method: 'GET',
            }),
            providesTags: ['RoomPlannerLayout'],
        }),
        updateLayout: builder.mutation<
            CreateLayoutResponse,
            CreateLayoutRequest
        >({
            query: ({roomId, ...data}) => ({
                url: `room-planner/layouts/${roomId}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['RoomPlannerLayout'],
        }),
        saveCabinetPosition: builder.mutation<
            RoomPlannerLayout,
            UpdateCabinetPositionRequest
        >({
            query: ({jobCabinetId, ...data}) => ({
                url: `room-planner/cabinets/${jobCabinetId}`,
                method: 'POST',
                body: data,
            }),
        }),
        savePlaceholderPosition: builder.mutation<
            {layout_room_placeholder: LayoutRoomPlaceholder},
            UpdatePlaceholderPositionRequest
        >({
            // Created without an id, then addressed by the id the create returns.
            query: ({id, ...data}) => ({
                url:
                    id != null
                        ? `room-planner/layout-placeholders/${id}`
                        : 'room-planner/layout-placeholders',
                method: 'POST',
                body: data,
            }),
        }),
        deletePlaceholderPosition: builder.mutation<void, number>({
            query: (id) => ({
                url: `room-planner/layout-placeholders/${id}`,
                method: 'DELETE',
            }),
        }),
        deleteLayout: builder.mutation<RoomPlannerLayout, number>({
            query: (layoutRoomId) => ({
                url: `room-planner/layouts/${layoutRoomId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['RoomPlannerLayout'],
        }),
        getRoomPlannerPDFStatus: builder.query<
            {isJobQueued: number},
            {roomId: number; timestamp: number}
        >({
            query: ({roomId}) => ({
                url: `room-planner/pdf/${roomId}/status`,
                method: 'GET',
            }),
        }),
        downloadRoomPlannerPDF: builder.query<
            {blob: Blob; headers: Headers},
            {id: number}
        >({
            query: ({id}) => {
                return {
                    url: `room-planner/pdf/${id}`,
                    cache: 'no-cache',
                    responseHandler: async (response) => {
                        const blob = await response.blob();
                        return {blob, headers: response.headers};
                    },
                };
            },
        }),
        shareRoomPlannerLink: builder.query<{qrUrl: string}, {id: number}>({
            query: ({id}) => {
                return {
                    url: `/room-planner/share/${id}`,
                };
            },
        }),
    }),
});

export const {
    useGetRoomPlannerPDFStatusQuery,
    useDuplicateProductMutation,
    useCreateLayoutMutation,
    useGetLayoutsQuery,
    useUpdateLayoutMutation,
    useSaveCabinetPositionMutation,
    useSavePlaceholderPositionMutation,
    useDeletePlaceholderPositionMutation,
    useDeleteLayoutMutation,
    useLazyDownloadRoomPlannerPDFQuery,
    useShareRoomPlannerLinkQuery,
} = roomPlannerApi;
